/** Kleine Vektorhilfen. Bewusst mutierend, wo es den heißen Pfad entlastet. */

import type { Vek } from '@pg/protocol';

export function vek(x = 0, y = 0): Vek {
  return { x, y };
}

export function laenge(v: Vek): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

export function abstand(a: Vek, b: Vek): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Quadrierter Abstand — für Vergleiche, spart die Wurzel. */
export function abstandQ(a: Vek, b: Vek): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

export function normiere(v: Vek): Vek {
  const l = laenge(v);
  if (l < 1e-9) return { x: 0, y: 0 };
  return { x: v.x / l, y: v.y / l };
}

/** Normiert v in-place. */
export function normiereZu(v: Vek): void {
  const l = laenge(v);
  if (l < 1e-9) {
    v.x = 0;
    v.y = 0;
    return;
  }
  v.x /= l;
  v.y /= l;
}

export function richtungVon(a: Vek, b: Vek): Vek {
  return normiere({ x: b.x - a.x, y: b.y - a.y });
}

export function winkelVon(v: Vek): number {
  return Math.atan2(v.y, v.x);
}

/** Kleinste Differenz zweier Winkel, in (-π, π]. */
export function winkelDiff(a: Vek, b: Vek): number {
  let d = winkelVon(b) - winkelVon(a);
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

/** Liegt `ziel` in einem Kegel um `richtung` mit halbem Öffnungswinkel? */
export function imKegel(von: Vek, richtung: Vek, ziel: Vek, halberWinkel: number): boolean {
  const zu = { x: ziel.x - von.x, y: ziel.y - von.y };
  const l = laenge(zu);
  if (l < 1e-9) return true;
  const punkt = (richtung.x * zu.x + richtung.y * zu.y) / l;
  return punkt >= Math.cos(halberWinkel);
}

export function begrenze(wert: number, min: number, max: number): number {
  return wert < min ? min : wert > max ? max : wert;
}

/** Lineare Annäherung — für weiche Ausrichtung ohne Ruckeln. */
export function annaehern(ist: number, soll: number, schritt: number): number {
  const d = soll - ist;
  if (Math.abs(d) <= schritt) return soll;
  return ist + Math.sign(d) * schritt;
}
